import { I18n } from '@calendly/i18n'
import { ArrowChevronLeftMiniIcon, ArrowChevronRightMiniIcon } from '@calendly/ui/components/icons'
import { useCalendarData } from '@calendly/ui/hooks/use-calendar-data'
import moment from 'moment-timezone'

import { DayLoader } from 'components/calendar/day_loader'

import { CalendarWrapper, Header, Heading, Table, NavigationWrapper, Button, Title, StyledDay } from './styles'

const Navigation = ({ backwardDisabled, forwardDisabled, onBackward, onForward, title }) => (
  <NavigationWrapper>
    <Button
      onClick={onBackward}
      disabled={backwardDisabled}
      aria-label={I18n.t('booking.templates.go_to_previous_month')}
    >
      <ArrowChevronLeftMiniIcon size="large" />
    </Button>
    <Title aria-live="assertive" aria-atomic="true" role="status">{title}</Title>
    <Button
      onClick={onForward}
      disabled={forwardDisabled}
      aria-label={I18n.t('booking.templates.go_to_next_month')}
    >
      <ArrowChevronRightMiniIcon size="large" />
    </Button>
  </NavigationWrapper>
)

const Week = ({ availableDays, selectedDates, week, toggleDay, isLoading }) => {
  const dayNames = I18n.t('date.day_names_hash')

  if (week.filter((day) => day.isInCurrentMonth).length === 0) {
    return null
  }

  return (
    <tr>
      {week.map(({ id, text, isBeforeToday, isToday, isInCurrentMonth, wday }) => {
        const disabled = isBeforeToday || !availableDays.includes(id) || isLoading
        return (
          <StyledDay
            key={id}
            bookable={!disabled}
            hidden={!isInCurrentMonth}
            disabled={disabled}
            selected={selectedDates.includes(id)}
            today={isToday}
            onClick={() => toggleDay(id)}
          >
            <span name={text} aria-label={`${dayNames[wday]} ${text}`}>{text}</span>
          </StyledDay>
        )
      }
      )}
    </tr>
  )
}

export const Calendar = ({ selectedDates, availableDays, timezone, currentMonthId, setCurrentMonthId, toggleDay, isLoading }) => {
  const {
    title, weekdays, weeks, nextMonthId, prevMonthId, isCurrentMonth
  } = useCalendarData(moment(currentMonthId).format('YYYY-MM'), timezone)

  return (
    <div data-component="calendar">
      <Header data-component="calendar-header">
        <Navigation
          backwardDisabled={isCurrentMonth}
          onBackward={() => setCurrentMonthId(prevMonthId)}
          onForward={() => setCurrentMonthId(nextMonthId)}
          title={title}
        />
      </Header>
      <CalendarWrapper>
        <Table>
          <thead>
            <tr>
              {weekdays.map((weekday) => <Heading key={weekday}>{weekday}</Heading>)}
            </tr>
          </thead>
          <tbody data-testid="calendar-table">
            {weeks.map((week) => (
              <Week
                key={week[0].id}
                week={week}
                selectedDates={selectedDates}
                availableDays={availableDays}
                toggleDay={toggleDay}
                isLoading={isLoading}
              />
            ))}
          </tbody>
        </Table>
        {isLoading && <DayLoader />}
      </CalendarWrapper>
    </div>
  )
}
